import type { SupportedLanguage } from "../../../../api/src/shared/domain/language";

/**
 * An announcement's artwork, per language.
 *
 * The hero is not decoration: the feed sheet's shows topic chips and the GPT
 * one shows a chat input, and both carry words. A French reader looking at
 * English chips is reading a screenshot of somebody else's app.
 *
 * Every language is spelled out, and `null` means "has none of its own, use
 * English". Optional keys would let a new locale be forgotten silently. This way
 * adding one is a type error in every announcement until each says what it does
 * about it, and `en` is required because it is what `null` resolves to.
 */
export type LocalizedHero = { en: string } & Record<
  Exclude<SupportedLanguage, "en">,
  string | null
>;

/** The artwork for a language, falling back to English where there is none. */
export function heroFor(
  hero: LocalizedHero,
  language: SupportedLanguage
): string {
  if (language === "en") return hero.en;
  const localized = hero[language];
  return localized === null ? hero.en : localized;
}

/**
 * How the reader left an announcement without acting on it. The three ways out
 * are kept apart because "tapped close", "swiped it away" and "tapped Not now"
 * are different signals: the last is a reader who read it and declined.
 */
export type AnnouncementDismissMethod =
  "close_button" | "backdrop_or_swipe" | "decline_button";

/**
 * The buttons an announcement can put in its footer, as stable ids.
 *
 * One list rather than one per component, because this is what the funnel
 * groups on: a translated label changes with the locale and with every copy
 * edit, and an id does not. A component picks the ones it shows; the host turns
 * the id into the analytics event.
 */
export const ANNOUNCEMENT_ACTIONS = [
  "explore-gpt",
  "follow-topics",
  "register-webinar",
] as const;

export type AnnouncementActionId = (typeof ANNOUNCEMENT_ACTIONS)[number];

/**
 * What every pop-up announcement component takes, and the whole contract
 * between one and `AnnouncementHost`.
 *
 * An announcement owns what it looks like, what it says, and where its buttons
 * go. It owns NOTHING about whether it belongs on screen, or about recording
 * that the reader is done with it — the host does both, which is why the two
 * ways out are props rather than something each component re-implements.
 *
 * `onDismiss` is the reader leaving without acting: the close button, the
 * backdrop, a swipe, or a "Not now" button. `onAct` is the reader taking the
 * announcement up on its offer. Both end the announcement for good; they are
 * kept apart because one is a conversion and the other is not.
 */
export type AnnouncementProps = {
  isOpen: boolean;
  onDismiss: (method: AnnouncementDismissMethod) => void;
  onAct: (action: AnnouncementActionId) => void;
};
